import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { MultiPageVideoModel, ElementTypes, VideoElement, AudioElement } from '@bynder-studio/render-core';
import SpecificationExporter from '@bynder-studio/render-core/src/SpecificationParser/SpecificationExporter';
import { List, Thumbnail, Flex, token, notify } from '@bynder/design-system';
import { IconArrowLeft, IconTextStyle, IconArrowRight } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { parseSrt, cuesToSrt } from './parseSrt';
import TranscriptGenerateModal from './TranscriptGenerateModal';
import TranscriptStyles from './TranscriptStyles';
import TranscriptForm from './TranscriptForm';
import { getCreative } from 'packages/store/creatives/creatives.selectors';
import { getElementsFonts, getFonts } from 'packages/store/user/user.selectors';
import useForceUpdate from '~/hooks/useForceUpdate';
import { getDefaultFontId } from 'packages/pages/editor/utils/getDefaultFontId';
import { StyledDivider } from 'packages/pages/components/Transcript/Component.styled';
import transcribeService from 'packages/services/transcribeService';
import { prepareDataForTranscribe } from '~/common/editor/editor.bloc';

const TRANSCRIBE_TIMEOUT_MS = 300_000; // 5 minutes

const waitForTranscribeCompleted = (jobId: number): Promise<void> =>
    new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            cleanup();
            reject(new Error('TRANSCRIBE_TIMEOUT'));
        }, TRANSCRIBE_TIMEOUT_MS);

        const cleanup = transcribeService.listenToTranscribeStatus(
            jobId,
            (status) => {
                if (status === 'COMPLETED') {
                    clearTimeout(timer);
                    resolve();
                } else if (status === 'RENDER_FAILED' || status === 'TRANSCRIPTION_FAILED') {
                    clearTimeout(timer);
                    reject(new Error(status));
                }
            },
            (error) => {
                clearTimeout(timer);
                reject(error);
            },
        );
    });

const fetchSrtFromTranscribeJob = async (jobId: number): Promise<string> => {
    const response = await transcribeService.getTranscribeUrl(jobId);

    if (response.status >= 400 || !response.json?.signedUrl) {
        throw new Error('TRANSCRIBE_URL_MISSING');
    }

    const srtResponse = await fetch(response.json.signedUrl);

    if (!srtResponse.ok) {
        throw new Error('FAILED_TO_FETCH_TRANSCRIBE_SRT');
    }

    const srt = await srtResponse.text();

    if (!srt.trim()) {
        throw new Error('TRANSCRIBE_RESULT_EMPTY');
    }

    return srt;
};

type TranscriptContext = 'editor' | 'content-configurator';

export const TranscriptListItem = ({
    creativeModel,
    onClick,
    context = 'editor',
    isInitialTemplateTranscriptEnabled = false,
}: {
    creativeModel: MultiPageVideoModel;
    onClick: () => void;
    context?: TranscriptContext;
    isInitialTemplateTranscriptEnabled?: boolean;
}) => {
    const { translate } = useTranslate();

    return (
        <List.Item
            thumbnail={<Thumbnail variant="clean" icon={<IconTextStyle />} />}
            onClick={onClick}
            subtext={
                creativeModel?.getTranscriptElement?.().enabled
                    ? translate('editor.transcript.subtext.enabled')
                    : translate('editor.transcript.subtext.disabled')
            }
            rightElements={<IconArrowRight />}
        >
            {translate('editor.transcript.title')}
        </List.Item>
    );
};

export const TranscriptBackButton = ({ onBack }: { onBack: () => void }) => {
    const { translate } = useTranslate();

    return (
        <List>
            <List.Item
                onClick={onBack}
                thumbnail={
                    <Flex alignItems="center">
                        <StyledBackIcon />
                        <Thumbnail variant="clean" icon={<IconTextStyle />} />
                    </Flex>
                }
            >
                {translate('editor.transcript.title')}
            </List.Item>
        </List>
    );
};

const Transcript = ({
    creativeModel,
    onBack,
    showEnabledToggle = true,
    showStyles = true,
    showBackButton = true,
    context = 'editor',
    variationId,
    pageIndex,
    isInitialTemplateTranscriptEnabled = false,
}: {
    creativeModel: MultiPageVideoModel;
    onBack: () => void;
    showEnabledToggle?: boolean;
    showStyles?: boolean;
    showBackButton?: boolean;
    context?: TranscriptContext;
    variationId?: string;
    pageIndex?: number;
    isInitialTemplateTranscriptEnabled?: boolean;
}) => {
    const { translate } = useTranslate();
    const [srtContent, setSrtContent] = useState('');
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const fonts = useSelector(getFonts);
    const elementsFonts = useSelector(getElementsFonts);
    const design = useSelector(getCreative);
    const forceUpdate = useForceUpdate();
    const transcriptElement = creativeModel.getTranscriptElement();

    const effectiveTranscriptEnabled = transcriptElement.enabled || isInitialTemplateTranscriptEnabled;
    const enabled = showEnabledToggle ? effectiveTranscriptEnabled : true;
    const shouldEnableTranscriptFromTemplate = isInitialTemplateTranscriptEnabled && !transcriptElement.enabled;

    useEffect(() => {
        if (shouldEnableTranscriptFromTemplate) {
            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                enabled: true,
            });
        }
    }, [creativeModel, shouldEnableTranscriptFromTemplate]);

    // Hydrate SRT from model cues whenever the active variation/page context changes.
    useEffect(() => {
        const cues = creativeModel.getTranscriptElement().cues;

        setSrtContent(cues?.length ? cuesToSrt(cues, creativeModel.getPlaybackDuration().frameRate) : '');
    }, [creativeModel, variationId, pageIndex]);

    // Keep SRT in sync when the model is updated externally (e.g. auto-generate).
    useEffect(() => {
        const currentModel = (creativeModel as any).getCurrentModel?.() ?? creativeModel;
        const emitter = currentModel.eventEmitter ?? currentModel;

        const handleTranscriptUpdated = ({ newValues }: { newValues: { cues?: any[] } }) => {
            if (!newValues.cues) {
                return;
            }

            setSrtContent(cuesToSrt(newValues.cues, creativeModel.getPlaybackDuration().frameRate));
        };

        emitter.on('transcriptUpdated', handleTranscriptUpdated);

        return () => {
            emitter.off('transcriptUpdated', handleTranscriptUpdated);
        };
    }, [creativeModel, pageIndex]);

    const hasTranscribableMedia = useMemo(() => {
        const currentModel = creativeModel.getCurrentModel?.();

        if (!currentModel) {
            return false;
        }

        const elements = currentModel.getAllElementsRecursively?.() || currentModel.getElements?.() || [];

        return elements.some((element: any) => {
            return element instanceof VideoElement || element instanceof AudioElement;
        });
    }, [creativeModel]);

    const defaultFontId = useMemo(() => getDefaultFontId(elementsFonts, fonts), [elementsFonts, fonts]);

    const handleToggleEnabled = useCallback(() => {
        const defaultProperties = transcriptElement.fontId === null ? { fontId: defaultFontId } : {};

        creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
            enabled: !enabled,
            ...defaultProperties,
        });
        forceUpdate();
    }, [creativeModel, enabled, transcriptElement?.fontId, defaultFontId, forceUpdate]);

    const handleOpenGenerateModal = useCallback(() => {
        setShowGenerateModal(true);
    }, []);

    const handleCancelGenerate = useCallback(() => {
        setShowGenerateModal(false);
    }, []);

    const frameRate = creativeModel.getPlaybackDuration().frameRate;
    const currentPageMeta = creativeModel.getModelsMetaData()[creativeModel.getCurrentPageIndex()];
    const pageName = currentPageMeta?.name || '';
    const designName = design?.name || '';

    const handleSrtChange = useCallback(
        (newSrtContent: string) => {
            setSrtContent(newSrtContent);

            if (!newSrtContent.trim()) {
                creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                    enabled: true,
                    cues: [],
                    fontId: transcriptElement.fontId ?? defaultFontId,
                });
                forceUpdate();

                return;
            }

            const cues = parseSrt(newSrtContent, frameRate);

            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                enabled: true,
                cues,
                fontId: transcriptElement.fontId ?? defaultFontId,
            });
            forceUpdate();
        },
        [creativeModel, defaultFontId, frameRate, forceUpdate, transcriptElement.fontId],
    );

    const handleGenerate = useCallback(
        async (languageCode: string) => {
            setIsGenerating(true);

            try {
                const rawElements = SpecificationExporter.exportElements(creativeModel.getCurrentModel().getElements());
                const { elements } = prepareDataForTranscribe(rawElements);
                const duration = creativeModel.getPlaybackDuration().duration;
                const createJobResponse =
                    context === 'editor'
                        ? await transcribeService.createTranscribeJob({
                              elements,
                              duration,
                              languageCode,
                          })
                        : variationId
                          ? await transcribeService.createVariationTranscribeJob(variationId, languageCode)
                          : null;

                if (!createJobResponse) {
                    throw new Error('VARIATION_ID_IS_MISSING');
                }

                if (createJobResponse.status >= 400 || !createJobResponse.json?.id) {
                    throw new Error('FAILED_TO_CREATE_TRANSCRIBE_JOB');
                }

                const jobId = createJobResponse.json.id;

                await waitForTranscribeCompleted(jobId);

                const resolvedSrt = await fetchSrtFromTranscribeJob(jobId);
                const cues = parseSrt(resolvedSrt, frameRate);

                if (!cues.length) {
                    throw new Error('TRANSCRIBE_RESULT_EMPTY');
                }

                setSrtContent(resolvedSrt);
                creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                    enabled: true,
                    cues,
                    fontId: defaultFontId,
                });

                setShowGenerateModal(false);
            } catch (error) {
                notify({
                    title: 'Transcript generation failed',
                    description: 'Please try again.',
                    variant: 'error',
                });
            } finally {
                setIsGenerating(false);
            }
        },
        [context, creativeModel, defaultFontId, frameRate, isInitialTemplateTranscriptEnabled, variationId],
    );

    return (
        <>
            {showBackButton && <TranscriptBackButton onBack={onBack} />}
            {showEnabledToggle && (
                <List>
                    <List.Item checkedVariant="switch" isChecked={enabled} onClick={handleToggleEnabled}>
                        {translate('editor.transcript.enable')}
                    </List.Item>
                </List>
            )}
            {enabled && (
                <TranscriptForm
                    srtContent={srtContent}
                    isGenerating={isGenerating}
                    frameRate={frameRate}
                    designName={designName}
                    pageName={pageName}
                    onGenerate={handleOpenGenerateModal}
                    onSrtChange={handleSrtChange}
                    isDisabled={!hasTranscribableMedia}
                />
            )}
            {enabled && showStyles && (
                <>
                    <StyledDivider />
                    <TranscriptStyles creativeModel={creativeModel} />
                </>
            )}
            {showGenerateModal && (
                <TranscriptGenerateModal
                    isGenerating={isGenerating}
                    onGenerate={handleGenerate}
                    onCancel={handleCancelGenerate}
                />
            )}
        </>
    );
};

const StyledBackIcon = styled(IconArrowLeft)`
    margin-right: ${token.spacing3};
`;

export default Transcript;
